import { PLAYBACK_STATE } from 'enums'
import { useCallback, useEffect, useRef } from 'react'
import { useSelector, useStore } from 'react-redux'
import { time2Pixel, secondsToTimelineTime } from '~/Util'
import * as Selectors from '~/selectors'
import { selectIsTimelinePlaying } from '~/selectors/playback'

export function useTimelineAutoScroll({ scrollbarRef, handleScrollLeft }) {
  const store = useStore()
  const playing = useSelector(selectIsTimelinePlaying)
  const projectDuration = useSelector(Selectors.getProjectDuration)
  const visibleTimelineDuration = useSelector(state => state.timeline.duration)
  const scale = useSelector(state => state.timeline.scale)
  const isRecordingStarted = useSelector(Selectors.recording.isRecordingStarted)
  const isRecordingSaving = useSelector(Selectors.recording.isRecordingSaving)
  const mediaRecordingAssetInProgress = isRecordingStarted || isRecordingSaving

  const stateRef = useRef({
    bounced: false,
    prevSliderTime: 0,
    handleScrollLeft,
  })

  const setScroll = useCallback(() => {
    const state = store.getState()
    const storeScrollLeft = state.timeline.scrollLeft

    if (scrollbarRef?.current && storeScrollLeft !== scrollbarRef.current.lastViewScrollLeft) {
      scrollbarRef.current.scrollLeft(storeScrollLeft)
    }
  }, [ scrollbarRef, store ])

  useEffect(() => {
    let unsubscribe = () => {}

    const handleChange = () => {
      const state = store.getState()

      const { sliderTime } = state.timeline
      const playing = state.playback.timelinePlaybackState === PLAYBACK_STATE.PLAY
      const mediaRecordingAssetInProgress = Selectors.recording.isRecordingStarted(state)
                                          || Selectors.recording.isRecordingSaving(state)
      const projectDuration = Selectors.getProjectDuration(state)
      const visibleTimelineDuration = state.timeline.duration

      const { prevSliderTime } = stateRef.current

      if (!playing) {
        setScroll()
        stateRef.current.bounced = false
        stateRef.current.prevSliderTime = sliderTime
        return
      }

      if (playing) {
        if (Math.abs(prevSliderTime - sliderTime) > secondsToTimelineTime(0.5)) {
          stateRef.current.bounced = false
          stateRef.current.prevSliderTime = sliderTime
          return
        }

        if (mediaRecordingAssetInProgress || projectDuration > visibleTimelineDuration) {
          if (!stateRef.current.bounced && (sliderTime >= visibleTimelineDuration)) {
            stateRef.current.bounced = true
            stateRef.current.handleScrollLeft(200)
          }

          if (stateRef.current.bounced) {
            const shift = Math.round(time2Pixel(sliderTime - prevSliderTime, scale)) // Предполагаемая утилита
            stateRef.current.handleScrollLeft(shift)
          }
        }
      }
      stateRef.current.prevSliderTime = sliderTime
    }

    unsubscribe = store.subscribe(handleChange)
    return () => unsubscribe()
  }, [
    playing, projectDuration, visibleTimelineDuration,
    scale, mediaRecordingAssetInProgress, handleScrollLeft, setScroll, store,
  ])

  return { setScroll }
}
